// src/utils/likeProvider.js

import React, { createContext, useContext, useEffect } from 'react';
import { auth, db } from '../firebase';
import {
  collection,
  doc,
  getDocs,
  updateDoc,
  arrayUnion,
  arrayRemove,
  increment,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { create } from 'zustand';

const useLikeStore = create((set, get) => ({
  likesMap: {},
  userLikedListings: new Set(),
  isInitialized: false,
  unsubscribe: null,

  initialize: () => {
    const { unsubscribe: currentUnsub } = get();
    if (currentUnsub) {
      currentUnsub();
    }

    let unsubAuth = null;
    let unsubLikes = null;

    const authUnsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        // Unsubscribe previous likes listener if exists
        if (unsubLikes) {
          unsubLikes();
          unsubLikes = null;
        }

        // Initialize user likes
        await get().initializeUserLikes(user.uid);
      } else {
        set({
          userLikedListings: new Set(),
          isInitialized: true,
        });
      }
    });

    set({
      unsubscribe: () => {
        authUnsubscribe();
        if (unsubLikes) {
          unsubLikes();
        }
      },
    });
  },

  initializeUserLikes: async (userId) => {
    try {
      const q = query(
        collection(db, 'listings'),
        where('likedBy', 'array-contains', userId)
      );
      const snap = await getDocs(q);
      const likedSet = new Set(snap.docs.map(doc => doc.id));
      set({
        userLikedListings: likedSet,
        isInitialized: true,
      });
    } catch (error) {
      console.error('LikeProvider init error:', error);
      set({ isInitialized: true });
    }
  },

  isLiked: (listingId) => {
    return get().userLikedListings.has(listingId);
  },

  getLikeCount: (listingId, currentCount) => {
    return currentCount;
  },

  toggleLike: async ({
    listingId,
    listingOwnerId,
    listingTitle,
    thumbnailUrl,
    listingImageUrl,
  }) => {
    const user = auth.currentUser;
    if (!user) return false;

    const isCurrentlyLiked = get().userLikedListings.has(listingId);
    const listingRef = doc(db, 'listings', listingId);

    // Optimistic UI update
    const { userLikedListings } = get();
    if (isCurrentlyLiked) {
      userLikedListings.delete(listingId);
    } else {
      userLikedListings.add(listingId);
    }
    set({ userLikedListings: new Set(userLikedListings) });

    try {
      if (isCurrentlyLiked) {
        await updateDoc(listingRef, {
          likedBy: arrayRemove(user.uid),
          likes: increment(-1),
        });
      } else {
        await updateDoc(listingRef, {
          likedBy: arrayUnion(user.uid),
          likes: increment(1),
        });

        // Send like notification only when liking (not unliking)
        if (listingOwnerId !== user.uid) {
          try {
            const { sendLikeNotification } = await import(
              '../services/notificationService'
            );
            await sendLikeNotification({
              listingOwnerId,
              listingId,
              listingTitle,
              thumbnailUrl,
              listingImageUrl,
            });
          } catch (error) {
            console.error('Failed to send like notification:', error);
          }
        }
      }
      return true;
    } catch (error) {
      // Revert on error
      const { userLikedListings: currentLiked } = get();
      if (isCurrentlyLiked) {
        currentLiked.add(listingId);
      } else {
        currentLiked.delete(listingId);
      }
      set({ userLikedListings: new Set(currentLiked) });
      console.error('LikeProvider toggleLike error:', error);
      return false;
    }
  },

  // Cleanup
  cleanup: () => {
    const { unsubscribe } = get();
    if (unsubscribe) {
      unsubscribe();
    }
    set({
      likesMap: {},
      userLikedListings: new Set(),
      isInitialized: false,
      unsubscribe: null,
    });
  },
}));

// React Hook for using the like store
export const useLike = () => {
  const store = useLikeStore();
  return {
    isLiked: store.isLiked,
    toggleLike: store.toggleLike,
    userLikedListings: store.userLikedListings,
    isInitialized: store.isInitialized,
    initialize: store.initialize,
    cleanup: store.cleanup,
  };
};

// React Hook for like status of a specific listing
export const useListingLike = (listingId) => {
  const { isLiked, toggleLike, isInitialized } = useLike();
  return {
    isLiked: isLiked(listingId),
    toggleLike: (params) => toggleLike({ ...params, listingId }),
    isInitialized,
  };
};

// Context Provider setup
const LikeContext = createContext(null);

export const LikeProvider = ({ children }) => {
  const store = useLikeStore();

  useEffect(() => {
    store.initialize();
    return () => {
      store.cleanup();
    };
  }, []);

  return (
    <LikeContext.Provider value={store}>
      {children}
    </LikeContext.Provider>
  );
};

export const useLikeContext = () => {
  const context = useContext(LikeContext);
  if (!context) {
    throw new Error('useLikeContext must be used within LikeProvider');
  }
  return context;
};

export default {
  LikeProvider,
  useLike,
  useLikeContext,
  useListingLike,
};